import { useState } from "react";
import classes from "./ceTransitions.module.scss";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

export default function CETransitions() {
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    city: "",
    industry: "",
    jobRole: "",
    country: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const phoneRegex =
    /^\+?\d{1,4}[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/;

  const keyToLabelMapping = {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email Address",
    company: "Company",
    phone: "Phone",
    city: "City",
    industry: "Industry",
    jobRole: "Job Role",
    country: "Country/Territory",
  };

  const requiredFields = [
    "firstName",
    "lastName",
    "company",
    "email",
    "phone",
    "city",
    "industry",
    "jobRole",
    "country",
  ];

  const handleClosePopup = () => {
    setIsSubmitted(false);
    setFormValues({
      firstName: "",
      lastName: "",
      company: "",
      email: "",
      phone: "",
      city: "",
      industry: "",
      jobRole: "",
      country: "",
    });
  };

  countries.registerLocale(enLocale);

  // Inside your component, get the list of countries
  const countryList = Object.entries(
    countries.getNames("en", { select: "official" })
  ).map(([code, name]) => ({
    code,
    name,
  }));

  // Sort the country list alphabetically by name
  countryList.sort((a, b) => a.name.localeCompare(b.name));

  const handleChange = (
    evt: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = evt.target;

    setIsSubmitted(false);
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!emailRegex.test(formValues.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!phoneRegex.test(formValues.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    requiredFields.forEach((field) => {
      if (!formValues[field as keyof typeof formValues]) {
        newErrors[field] = `${
          keyToLabelMapping[field as keyof typeof keyToLabelMapping]
        } is required`;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const sendData = async () => {
        await fetch("https://ngainews.com/api/v1/createEventAttendesData", {
          method: "POST",
          body: JSON.stringify({
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            company: formValues.company,
            email: formValues.email,
            phone: formValues.phone,
            city: formValues.city,
            industry: formValues.industry,
            jobRole: formValues.jobRole,
            country: formValues.country,
            event: "ai-data-centers-to-accelerate-ce-transitions",
          }),
          headers: myHeaders,
        })
          .then((res) => {
            if (res.status === 200) {
              setIsSubmitted(true);
              if (isSubmitted) {
                toast.success("Data submitted successfully");
              }
            } else {
              throw new Error("Error while submitting data");
            }
          })
          .catch((err) => {
            toast.error(err.message);
          })
          .finally(() => {
            setIsLoading(false);
          });
      };

      sendData();
    }
  };

  return (
    <>
      <div className={classes["container"]}>
        <div className={classes["left-panel"]}>
          <div className={classes["banner"]}>
            <img
              src="/webpage-banner.png"
              alt="Banner"
              width={1000}
              height={385}
            />
          </div>

          <div className={classes["section"]}>
            <h2>About</h2>
            <div className={classes["SectionDivider"]}></div>
            <p className={classes["subtextdivider"]}>
              <strong>Start date:</strong> Thursday, June 26, 2025
            </p>
            <p className={classes["subtextdivider"]}>
              <strong>Start time:</strong> 09:00 AM BST
            </p>
            <p className={classes["subtextdivider"]}>
              <strong>Duration:</strong> 120 minutes
            </p>
            <p className={classes["subtextdivider"]}>
              <strong>Event Format:</strong> Online
            </p>
            <h2>Session 1</h2>
            <p className={classes["subtextdivider"]}>
              <strong>Hosted By:</strong> Mr. Kasu Venkata Reddy, Co-founder &
              CEO, Sustainability Economics.ai
            </p>
            <p className={classes["subtextdivider"]}>
              <strong>Overview:</strong>
            </p>
            <p className={classes["para-1"]}>
              As the demand for AI computing intensifies, AI data centers are
              emerging as some of the most energy-intensive infrastructures
              globally. To meet this surging demand sustainably, accelerating
              the adoption of clean energy solutions are no longer optional;
              they are essential.
            </p>
            <p className={classes["para-2"]}>
              In response, AI data centers are moving closer to clean energy
              sources.
            </p>
            <p className={classes["subtextdivider"]}>
              <strong>What to Expect:</strong>
            </p>
            <ul className={classes["list-items"]}>
              <li>
                An introduction to how the AI data center boom is transforming
                the global energy landscape
              </li>
              <li>
                An overview of how CLEM.ai can identify globally distributed,
                economically viable clean energy sources meeting the demand.
              </li>
              <li>
                A preview of NG Infra.ai — a platform that identifies optimal
                locations for AI data centers based on infrastructure, develops
                optimal design structures with cost estimates, and ensures
                bankability while raising financing.
              </li>
            </ul>
            <h2>Session 2</h2>
            <p className={classes["subtextdivider"]}>
              <strong>Overview:</strong>
            </p>
            <p className={classes["para-3"]}>
              This panel will bring together experts from finance, energy, and
              AI infrastructure to discuss how each stakeholder group can
              leverage the rise of AI data centers to enable clean energy
              transitions.
            </p>
            <p className={classes["subtextdivider"]}>
              <strong>What’s in it for Stakeholders:</strong>
            </p>
            <ul className={classes["list-items"]}>
              <li>
                <strong>Financial Institutions:</strong> Explore how to identify
                and finance new bankable opportunities at the intersection of AI
                data center growth and clean energy supply
              </li>
              <li>
                <strong>Fossil Fuel Asset Owners:</strong> Understand how to
                transition from traditional assets to clean energy
                infrastructure projects tied to AI demand
              </li>
              <li>
                <strong>Clean Energy Developers:</strong> Explore how to scale
                faster by tapping into the surging energy requirements of AI
                ecosystems
              </li>
              <li>
                <strong>Hyperscalers, Enterprises & Service Providers:</strong>{" "}
                Discover pathways to expand AI data center footprints more
                sustainably and gain long-term resilience and cost-efficiency
              </li>
            </ul>
            <div className={classes["agenda"]}>
              <h2>Event Agenda</h2>
              <div className={classes["SectionDivider"]}></div>
              <p>
                <strong>9:00 AM – 9:05 AM: Opening Remarks</strong>
                <br />
                Welcome and introduction to the event.
              </p>
              <p>
                <strong>9:05 AM – 9:45 AM: Keynote Session</strong>
                <br />
                Presented by Mr. Kasu Venkata Reddy, Co-founder and CEO.
              </p>
              <p>
                <strong>9:45 AM – 9:55 AM: Break</strong>
                <br />
                Short intermission for attendees.
              </p>
              <p>
                <strong>9:55 AM – 10:00 AM: Panel Introduction</strong>
                <br />
                Overview of the panel discussion and speakers.
              </p>
              <p>
                <strong>10:00 AM – 10:45 AM: Panel Discussion</strong>
                <br />
                Featuring industry experts across clean energy, finance, and AI
                infrastructure.
              </p>
              <p>
                <strong>10:45 AM – 10:55 AM: Audience Q&A</strong>
                <br />
                Interactive session with audience questions.
              </p>
              <p>
                <strong>10:55 AM – 11:00 AM: Closing Remarks</strong>
                <br />
                Summary and closing thoughts.
              </p>
            </div>
          </div>

          <div className={classes["speaker"]}>
            <div className={classes["speaker-title-container"]}>
              <h2>Speakers</h2>
              <div className={classes["SectionDivider"]}></div>
            </div>
            <div className={classes["speakercontainer-wrapper"]}>
              <div className={classes["speaker-scroll-container"]}>
                <center className={classes["customSpeakerImageWrapper"]}>
                  <img
                    src="/ceo-image.png"
                    alt="Speaker"
                    width={400}
                    height={400}
                  />
                </center>
                <p className={classes["customSpeakerName"]}>
                  Venkata Kasu Reddy
                </p>
                <p className={classes["customSpeakerTitle"]}>
                  Co-founder and CEO
                </p>
                <p className={classes["customSpeakerCompany"]}>
                  Sustainability Economics.ai
                </p>
                <div className={classes["customSpeakerBio"]}>
                  A seasoned global corporate C-suite executive with over 25
                  years of visionary leadership experience, specialising in
                  strategic planning, financial expertise, market expansion, and
                  solving business challenges through cutting-edge technologies.
                  Previously served as CTO of Cisco, leading multi-billion
                  dollar business initiatives on a global scale.
                </div>
              </div>
              <div className={classes["speaker-scroll-container"]}>
                <center className={classes["customSpeakerImageWrapper"]}>
                  <img
                    src="/kenji-profile.png"
                    alt="Speaker"
                    width={400}
                    height={400}
                  />
                </center>
                <p className={classes["customSpeakerName"]}>Kenji Irie</p>
                <p className={classes["customSpeakerTitle"]}>
                  Executive Officer and General Manager
                </p>
                <p className={classes["customSpeakerTitle"]}>
                  Sustainability Planning Department
                </p>
                <p className={classes["customSpeakerCompany"]}>
                  Sumitomo Mitsui Financial Group
                </p>
                <p className={classes["customSpeakerCompany"]}>
                  Sumitomo Mitsui Banking Corporation
                </p>
                <div className={classes["customSpeakerBio"]}>
                  Kenji Irie is an Executive Officer and General Manager of
                  Sustainability Planning Department. Based in Tokyo, Kenji
                  leads SMBC Group’s corporate strategy on sustainability and
                  social issues with responsibilities that include target
                  setting, governance control, risk management{" "}
                  <span className={classes["tooltip"]}>
                    [<strong>more</strong>...]
                    <span className={classes["tooltiptext"]}>
                      and disclosure, as well as new business development and
                      CSR activities. Prior to his current role, Kenji was a
                      Managing Director, General Manager, and Co-Head of
                      Corporate and Investment Banking Coverage for SMBC
                      Americas Division in New York, where he co-led the firm’s
                      U.S. client coverage activities across both the corporate
                      and investment banking platforms.
                      <br />
                      Previously, Kenji was based in Tokyo from 2013 to 2020 and
                      played senior management roles such as Joint General
                      Manager with the Planning Department, International
                      Banking Unit, where he was responsible for strategic
                      planning and business development within the Americas and
                      EMEA markets, and Senior Vice President in SMBC’s
                      Corporate Planning Department.
                      <br />
                      Prior to his roles in Tokyo, Kenji was based in New York,
                      holding several senior positions from 1998 to 2013. He was
                      an Executive Director and Head of Corporate Development in
                      the Planning Department Americas Division, where he was
                      responsible for strategic planning and business
                      development within the Americas. Before joining the
                      Planning Department, Kenji was an Executive Director and
                      Head of Chemicals, Materials and Engineering, &
                      Construction Coverage for the U.S. Corporate Banking
                      Department, where he managed client coverage in these
                      sectors.
                      <br />
                      Kenji developed extensive client coverage and business
                      development experience throughout his more 25 years in
                      financial services. Kenji earned a Bachelor of Economics
                      from Kobe University.
                    </span>
                  </span>
                </div>
              </div>
              </div>
              <div className={classes["speakercontainer-wrapper"]}>
                <div className={classes["speaker-scroll-container"]}>
                    <center className={classes["customSpeakerImageWrapper"]}>
                      <img
                        src="/henri-de-branche.png"
                        alt="Speaker"
                        width={400}
                        height={400}
                      />
                    </center>
                    <p className={classes["customSpeakerName"]}>Henri de Branche</p>
                    <p className={classes["customSpeakerTitle"]}>
                      Managing Director
                    </p>
                    <p className={classes["customSpeakerCompany"]}>
                      Asia Pacific Head of <br />
                      Sustainable Solutions
                    </p>
                    <p className={classes["customSpeakerCompany"]}>J.P. Morgan</p>
                    <div className={classes["customSpeakerBio"]}>
                      Henri de Branche is the Head of Sustainable Solutions in Asia
                      Pacific, where he leads the firm’s efforts with clients to
                      enhance sustainability performance and align their strategies
                      most favorably for sustainability-focused/responsible
                      investors in the scope of financing, capital markets and M&A
                      transactions.{" "}
                      <span className={classes["tooltip"]}>
                        [<strong>more</strong>...]
                        <span className={classes["tooltiptext"]}>
                          He also oversees the operationalization of J.P. Morgan’s
                          climate commitments in line with the Paris Agreement with
                          the Centre for Carbon Transition, engaging with clients in
                          hard-to-abate sectors on transition-related financing,
                          research and advisory solutions.
                          <br />
                          Henri has over 20 years of experience in industry,
                          consulting and financial services based in Asia, and has
                          helped over 100 regional clients across industries improve
                          their sustainability credentials, optimize value and
                          mitigate risk. Before joining J.P. Morgan, he was a
                          Partner with ERM, responsible for Sustainable Finance
                          advisory based in Beijing. He also participated in the
                          establishment of the ESG function at the Asian
                          Infrastructure Investment Bank (AIIB).
                        </span>
                      </span>
                    </div>
                </div>
                <div className={classes["speaker-scroll-container"]}>
                  <center className={classes["customSpeakerImageWrapper"]}>
                    <img
                      src="/christina-ng.png"
                      alt="Speaker"
                      width={400}
                      height={400}
                    />
                  </center>
                  <p className={classes["customSpeakerName"]}>Christina Ng</p>
                  <p className={classes["customSpeakerTitle"]}>
                    Managing Director & Co-Founder
                  </p>
                  <p className={classes["customSpeakerCompany"]}>
                    Energy Shift Institute
                  </p>
                  <div className={classes["customSpeakerBio"]}>
                  Christina Ng is the Co-Founder of the Energy Shift Institute, an independent, public-interest nonprofit think tank advancing Southeast Asia’s energy transition through contextualised insights and regional perspectives. Her work centres on strengthening the role of financial markets in supporting Southeast Asia’s economic transformation, drawing on her deep understanding of regional market dynamics and institutional frameworks.
                  {" "}
                    <span className={classes["tooltip"]}>
                      [<strong>more</strong>...]
                      <span className={classes["tooltiptext"]}>
                        Christina is a regular commentator on sustainable finance, with insights featured in Reuters, the Financial Times and Bloomberg. Christina has contributed to the development of more globally harmonised green finance standards, challenged conventional energy finance practices, raised greenwashing awareness, and advised corporations, financial institutions and policymakers. She is a member of the SBTi Financial Institution Net-Zero Expert Advisory Group.
                        With over 20 years of experience across standard-setting, research, financial risk and advisory, she engages widely with public and private sector leaders across Asia Pacific.
                      </span>
                    </span>
                  </div>
                </div>
              </div>
          </div>



          <div className={classes["company-details"]}>
            <div className={classes["company-title-container"]}>
              <h2 className={classes["company-title"]}>
                About Sustainability Economics.ai
              </h2>
              <div className={classes["SectionDivider"]}></div>
            </div>
            <div>
              <p className={classes["company-description"]}>
                We specialize in comprehensive, end-to-end solutions that
                prioritize sustainability and leverage cutting - edge AI
                technologies. Our innovative ‘connect the dots’ methodology,
                combined with our proactive ‘make it happen’ mindset, allows us
                to tackle complex challenges and deliver transformative results.
                Our vision is to accelerate the shift to a low-carbon economy by
                advancing clean energy solutions and building AI systems that
                help businesses operate sustainably, adapt strategically, and
                achieve their net-zero goals.
              </p>
            </div>
          </div>
        </div>

        <div className={classes["right-panel"]}>
          {isSubmitted ? (
            <div className={classes["popup"]}>
              <div className={classes["popup-content"]}>
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" fill="#22c55e" />
                  <path
                    d="M10 14.5L6.5 11L7.91 9.59L10 11.67L15.09 6.59L16.5 8L10 14.5Z"
                    fill="white"
                  />
                </svg>
                <h2>Registration Successful!</h2>
                <p>
                  Thankyou for registering for the event. You'll receive a
                  confirmation email with event details soon.
                </p>
                <button onClick={handleClosePopup}>Close</button>
              </div>
            </div>
          ) : (
            <>
              <h2 className={classes["register-here"]}>Register here.</h2>
              <form onSubmit={handleSubmit}>
                {[
                  { label: "First Name", id: "firstName", type: "text" },
                  { label: "Last Name", id: "lastName", type: "text" },
                  { label: "Company", id: "company", type: "text" },
                  { label: "Email Address", id: "email", type: "email" },
                  { label: "Phone", id: "phone", type: "tel" },
                  { label: "City", id: "city", type: "text" },
                ].map(({ label, id, type }) => (
                  <div className={classes["form-group"]} key={id}>
                    <label htmlFor={id}>{label} *</label>
                    <input
                      type={type}
                      id={id}
                      name={id}
                      value={formValues[id as keyof typeof formValues]}
                      onChange={handleChange}
                      required
                    />
                    {errors[id] && (
                      <p className={classes.error}>{errors[id]}</p>
                    )}
                  </div>
                ))}

                <div className={classes["form-group"]}>
                  <label htmlFor="industry">Industry *</label>
                  <select
                    id="industry"
                    name="industry"
                    value={formValues.industry}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Industry --</option>
                    <option value="energy">Energy</option>
                    <option value="technology">Technology</option>
                    <option value="finance">Finance</option>
                    <option value="consulting">Consulting</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="government">Government</option>
                    <option value="education">Education</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="telecommunications">
                      Telecommunications
                    </option>
                    <option value="transportation">Transportation</option>
                    <option value="real_estate">Real Estate</option>
                    <option value="non_profit">Non-Profit</option>
                    <option value="media">Media & Publishing</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.industry && (
                    <p className={classes.error}>{errors.industry}</p>
                  )}
                </div>

                <div className={classes["form-group"]}>
                  <label htmlFor="jobRole">Job Role *</label>
                  <select
                    id="jobRole"
                    name="jobRole"
                    value={formValues.jobRole}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Job Role --</option>
                    <option value="executive">
                      Executive (CEO, CFO, Founder, etc.)
                    </option>
                    <option value="manager">Manager</option>
                    <option value="director">Director</option>
                    <option value="analyst">Analyst</option>
                    <option value="engineer">Engineer</option>
                    <option value="consultant">Consultant</option>
                    <option value="researcher">Researcher</option>
                    <option value="academic">Academic / Educator</option>
                    <option value="policy_maker">
                      Policy Maker / Government
                    </option>
                    <option value="student">Student</option>
                    <option value="media">Journalist / Media</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.jobRole && (
                    <p className={classes.error}>{errors.jobRole}</p>
                  )}
                </div>

                <div className={classes["form-group"]}>
                  <label htmlFor="country">Country/Territory *</label>
                  <select
                    id="country"
                    name="country"
                    value={formValues.country}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Country --</option>
                    {countryList.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {errors.country && (
                    <p className={classes.error}>{errors.country}</p>
                  )}
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    margin: "1rem 0",
                  }}
                >
                  By submitting this form, you consent to receiving
                  communications related to this event. Your data will not be
                  shared with third parties.
                </p>

                <button type="submit" disabled={isLoading || isSubmitted}>
                  {isLoading
                    ? "Submitting..."
                    : isSubmitted
                    ? "Submitted"
                    : "SUBMIT"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      <footer className={classes["footer"]}>
        <p>
          Content ©2025{" "}
          <a
            href="https://www.sustainabilityeconomics.ai"
            className={classes["link"]}
            target="_blank"
            rel="noopener noreferrer"
          >
            <strong>Sustainability Economics Pte Ltd.</strong>
          </a>{" "}
          All rights reserved.
        </p>
      </footer>
    </>
  );
}
